import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Typed-confirmation destructive-action modal.
 *
 * Props:
 *   open            : bool — controls visibility
 *   onClose         : () => void
 *   onConfirm       : ({ confirm, reason }) => Promise<void>
 *   title           : string — modal heading (e.g. "Delete channel")
 *   description     : ReactNode — body copy explaining the consequences
 *   confirmText     : string — the EXACT text the admin must type to enable Delete
 *   confirmLabel    : string — what to call the value (e.g. "channel name", "user email")
 *   requireReason   : bool — show + require a reason textarea
 *   reasonPlaceholder
 *   busy            : bool — disables buttons while the request is in flight
 *   testIdPrefix    : string — for data-testid on inputs/buttons
 */
export default function ConfirmDeleteModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  confirmLabel,
  requireReason = false,
  reasonPlaceholder = "Reason (visible only to admins in audit log)",
  busy = false,
  testIdPrefix = "confirm-delete",
}) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) { setTyped(""); setReason(""); }
  }, [open]);

  if (!open) return null;

  const matches = typed.trim().toLowerCase() === (confirmText || "").trim().toLowerCase();
  const ready = matches && (!requireReason || reason.trim().length > 0);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid={`${testIdPrefix}-overlay`}
    >
      <div className="card-surface w-full max-w-lg p-6 sm:p-8 relative" data-testid={`${testIdPrefix}-modal`}>
        <button
          onClick={onClose}
          disabled={busy}
          className="absolute top-4 right-4 text-white/40 hover:text-white"
          aria-label="Close"
          data-testid={`${testIdPrefix}-close`}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0 bg-red-500/15 border border-red-500/30">
            <AlertTriangle className="h-5 w-5 text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-2xl font-bold leading-tight">{title}</h3>
            <div className="text-sm text-white/60 mt-2 space-y-2">{description}</div>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label className="text-eyebrow block mb-2">
              Type the {confirmLabel} to confirm:{" "}
              <span className="font-mono text-white/70 break-all">{confirmText}</span>
            </label>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="input-dark"
              placeholder={confirmText}
              disabled={busy}
              data-testid={`${testIdPrefix}-input`}
            />
          </div>

          {requireReason && (
            <div>
              <label className="text-eyebrow block mb-2">Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="input-dark resize-none"
                placeholder={reasonPlaceholder}
                maxLength={500}
                disabled={busy}
                data-testid={`${testIdPrefix}-reason`}
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="ghost-btn"
            data-testid={`${testIdPrefix}-cancel`}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm({ confirm: typed.trim(), reason: reason.trim() })}
            disabled={!ready || busy}
            className="ember-btn bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`${testIdPrefix}-submit`}
          >
            {busy ? "Working..." : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
